import { loadStripe, Stripe } from '@stripe/stripe-js'
import { all, call, delay, put, select, takeLatest } from 'redux-saga/effects'
import { IActivePass } from '../../models'
import { LiteralsService, PassesService } from '../../services'
import {
  buyPassFailedAction,
  buyPassSuccessfulAction,
  fetchActivePassesSuccessfulAction,
  fetchAvailablePassesSuccessfulAction,
  setBuyPassConfirmingAction,
  setBuyPassPollingAction,
  setIsBuyingPassAction,
  setIsFetchingActivePassesAction,
  setIsFetchingAvailablePassesAction,
  setNotificationAction
} from '../actions'
import { IStoreState } from '../states'
import { IBuyPassAction, WalletTypeKeys } from '../types'

// The pass appears in the BE a moment after Stripe confirms, so the list is polled.
const POLL_INTERVAL = 2000
const POLL_TIMEOUT = 30000

function* fetchActivePassesAsync(): any {
  let passes: IActivePass[] = []
  try {
    yield put(setIsFetchingActivePassesAction())
    passes = yield call(PassesService.getActivePasses)
  } catch (error) {
    yield put(
      setNotificationAction({
        isError: true,
        label: LiteralsService.get('serverError', true)
      })
    )
  }
  yield put(fetchActivePassesSuccessfulAction(passes))
}

function* fetchAvailablePassesAsync(): any {
  let passes: any = []
  try {
    yield put(setIsFetchingAvailablePassesAction())
    passes = yield call(PassesService.getAvailablePasses)
  } catch (error) {
    yield put(
      setNotificationAction({
        isError: true,
        label: LiteralsService.get('serverError', true)
      })
    )
  }
  yield put(fetchAvailablePassesSuccessfulAction(passes))
}

/**
 * Each pass group is sold by its own client, with its own Stripe connected account, which
 * need not be the one the app was mounted with. A Stripe instance for that account is loaded
 * on demand; the app-wide one only serves as the fallback.
 */
function* stripeForAccount(accountId: string): any {
  const stripeKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY

  if (accountId && stripeKey) {
    const stripe: Stripe | null = yield call(loadStripe, stripeKey, { stripeAccount: accountId })
    if (stripe) return stripe
  }

  return yield select((state: IStoreState) => state.configuration.stripe)
}

async function confirmPassPayment(stripe: Stripe, clientSecret: string) {
  return stripe.confirmPayment({ clientSecret, redirect: 'if_required' })
}

function* buyPassAsync(action: IBuyPassAction): any {
  const { passId, accountId } = action.payload
  const currentPasses: IActivePass[] = yield select((state: IStoreState) => state.wallet.activePasses)
  const previousCount = currentPasses.length

  try {
    yield put(setIsBuyingPassAction())
    const { clientSecret } = yield call(PassesService.buyPass, passId)

    if (!clientSecret) {
      yield put(buyPassFailedAction())
      yield put(
        setNotificationAction({
          isError: true,
          label: LiteralsService.get('passBuyFailed', true)
        })
      )
      return
    }

    const stripe: Stripe = yield call(stripeForAccount, accountId)
    if (!stripe) {
      yield put(buyPassFailedAction())
      yield put(
        setNotificationAction({
          isError: true,
          label: LiteralsService.get('passBuyFailed', true)
        })
      )
      return
    }

    yield put(setBuyPassConfirmingAction())
    const { paymentIntent, error: stripeError } = yield call(confirmPassPayment, stripe, clientSecret)

    if (stripeError || paymentIntent?.status !== 'succeeded') {
      yield put(buyPassFailedAction())
      yield put(
        setNotificationAction({
          isError: true,
          label: stripeError?.message || LiteralsService.get('passBuyFailed', true)
        })
      )
      return
    }

    yield put(buyPassSuccessfulAction())
    yield put(
      setNotificationAction({
        isError: false,
        label: LiteralsService.get('passBuySuccess', true)
      })
    )

    yield put(setBuyPassPollingAction())
    const startTime = Date.now()

    while (Date.now() - startTime < POLL_TIMEOUT) {
      yield delay(POLL_INTERVAL)
      try {
        const passes: IActivePass[] = yield call(PassesService.getActivePasses)
        if (passes.length > previousCount) {
          yield put(fetchActivePassesSuccessfulAction(passes))
          return
        }
      } catch (pollError) {
        // Keep polling: a transient failure must not end the wait early.
      }
    }

    // Timed out — read once more, and stop loading whatever happens.
    try {
      const passes: IActivePass[] = yield call(PassesService.getActivePasses)
      yield put(fetchActivePassesSuccessfulAction(passes))
    } catch (error) {
      yield put(fetchActivePassesSuccessfulAction(currentPasses))
    }
  } catch (error: any) {
    const errorMessage = error?.message || ''
    let errorLabel = 'passBuyFailed'
    if (errorMessage.includes('404')) errorLabel = 'passNotFound'
    else if (errorMessage.includes('400')) errorLabel = 'passAlreadyActive'
    else if (errorMessage.includes('422')) errorLabel = 'passPaymentError'

    yield put(buyPassFailedAction())
    yield put(
      setNotificationAction({
        isError: true,
        label: LiteralsService.get(errorLabel, true)
      })
    )
  }
}

function* watchFetchActivePassesAsync() {
  yield takeLatest(WalletTypeKeys.FETCH_ACTIVE_PASSES, fetchActivePassesAsync)
}

function* watchFetchAvailablePassesAsync() {
  yield takeLatest(WalletTypeKeys.FETCH_AVAILABLE_PASSES, fetchAvailablePassesAsync)
}

function* watchBuyPassAsync() {
  yield takeLatest(WalletTypeKeys.BUY_PASS, buyPassAsync)
}

export default function* rootSaga() {
  yield all([
    watchFetchActivePassesAsync(),
    watchFetchAvailablePassesAsync(),
    watchBuyPassAsync()
  ])
}
